import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { API_ENDPOINTS } from '../../utils/config';
import { generateDataSheetPDF } from './generatePDF';
import { getMediaUrl, getAuthenticatedImageUrl, imageToBase64 as imageToBase64Util, fetchImageBlob } from '../../utils/imageUtils';

const FieldSurvey = () => {
  const [surveyData, setSurveyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [documentImageSrc, setDocumentImageSrc] = useState(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  // Get survey ID from URL parameters
  const getSurveyId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('surveyId');
  };

  // Fetch survey details from API
  const fetchSurveyDetails = async () => {
    const surveyId = getSurveyId();
    if (!surveyId) {
      setError('Survey ID not found in URL');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.SURVEY_DETAIL(surveyId), { headers });
      setSurveyData(response.data);
    } catch (error) {
      // Failed to fetch survey details handled silently
      setError('Failed to load survey details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveyDetails();
  }, []);

  const handleBackToList = () => {
    window.location.href = '/surveys';
  };

  // Use centralized getMediaUrl from utils

  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setDocumentImageSrc(getMediaUrl(document.file_path));
    setIsDocumentModalOpen(true);
  };

  const closeDocumentModal = () => {
    setSelectedDocument(null);
    setDocumentImageSrc(null);
    setIsDocumentModalOpen(false);
  };

  // Use centralized imageToBase64 from utils
  const imageToBase64 = imageToBase64Util;

  const handleDownloadAllImages = async () => {
    const imageDocs = (surveyData.documents || []).filter(doc => doc.file_path);
    if (!imageDocs.length) return;
    setIsDownloadingAll(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      await Promise.all(imageDocs.map(async (doc) => {
        try {
          const blob = await fetchImageBlob(doc.file_path);
          if (!blob) return;
          const ext = doc.file_path.split('.').pop().split('?')[0] || 'jpg';
          const fileName = `${doc.title || doc.file_path.split('/').pop()}.${ext}`.replace(/\.{2,}/, '.');
          zip.file(fileName, blob);
        } catch { /* skip failed */ }
      }));
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Survey_${surveyData.survey_number || 'Images'}_Documents.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingAll(false);
    }
  };

  // Helper function to add table with borders
  const addTable = (doc, data, x, y, colWidths, fontSize = 9) => {
    let currentY = y;
    doc.setFontSize(fontSize);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.1);
    
    data.forEach((row, rowIndex) => {
      let currentX = x;
      row.forEach((cell, colIndex) => {
        const width = colWidths[colIndex];
        const height = 6;
        
        // Draw cell border
        doc.rect(currentX, currentY, width, height);
        
        // Add text
        doc.setFont(undefined, rowIndex === 0 ? 'bold' : 'normal');
        const text = String(cell || '');
        const textLines = doc.splitTextToSize(text, width - 2);
        const textY = currentY + (height / 2) + (textLines.length > 1 ? 2 : 3);
        doc.text(textLines, currentX + 1, textY, { maxWidth: width - 2 });
        
        currentX += width;
      });
      currentY += height;
    });
    
    return currentY;
  };

  // Generate PDF Report
  const generatePDFReport = async () => {
    if (!surveyData) return;

    setIsGeneratingPDF(true);
    try {
      const doc = await generateDataSheetPDF(surveyData, imageToBase64);
      
      // Save PDF
      const fileName = `Survey_${surveyData.survey_number || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: 'Play' },
      completed: { color: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: 'XCircle' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon name={config.icon} size={12} className="mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-screen">
          <div className="flex items-center space-x-2">
            <Icon name="Loader2" size={24} className="animate-spin" color="var(--color-primary)" />
            <span className="text-foreground">Loading survey details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={24} color="var(--color-red-600)" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">Error Loading Survey</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <div className="flex items-center justify-center space-x-2">
              <Button variant="outline" onClick={fetchSurveyDetails}>
                Try Again
              </Button>
              <Button variant="default" onClick={handleBackToList}>
                Back to Surveys
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="FileX" size={24} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">Survey Not Found</h3>
            <p className="text-muted-foreground mb-4">The requested survey could not be found.</p>
            <Button variant="default" onClick={handleBackToList}>
              Back to Surveys
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToList}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Surveys
              </Button>
              <div className="flex items-center space-x-2">
                <Icon name="ClipboardList" size={24} color="var(--color-primary)" />
                <h1 className="text-2xl font-semibold text-foreground">Survey Details</h1>
              </div>
              {getStatusBadge(surveyData.status)}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="default"
                size="sm"
                onClick={generatePDFReport}
                disabled={isGeneratingPDF || !surveyData}
                iconName="Download"
                iconPosition="left"
              >
                {isGeneratingPDF ? 'Generating PDF...' : 'Download Report'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchSurveyDetails}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Survey Information */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Survey Overview */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Survey Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Survey Number</label>
                    <p className="text-foreground font-mono">{surveyData.survey_number}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Survey Date</label>
                    <p className="text-foreground">{formatDate(surveyData.survey_date)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Created</label>
                    <p className="text-foreground">{formatDateTime(surveyData.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                    <p className="text-foreground">{formatDateTime(surveyData.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Property Details</h2>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Client</label>
                    <p className="text-foreground">{surveyData.property_details?.client}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Customer Name</label>
                    <p className="text-foreground">{surveyData.property_details?.customer_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Contact Person</label>
                    <p className="text-foreground">{surveyData.property_details?.contact_person_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone Number</label>
                    <p className="text-foreground">{surveyData.property_details?.contact_person_phone_num}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Property Address</label>
                    <p className="text-foreground">{surveyData.property_details?.property_address}</p>
                  </div>
                </div>
              </div>

              {/* Surveyor Information */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Surveyor Information</h2>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{surveyData.surveyor?.username}</p>
                    <p className="text-sm text-muted-foreground">{surveyData.surveyor?.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Location */}
              {surveyData.latitude && surveyData.longitude && (
                <div className="bg-card rounded-lg border border-border p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Location</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Latitude</label>
                      <p className="text-foreground font-mono">{surveyData.latitude}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Longitude</label>
                      <p className="text-foreground font-mono">{surveyData.longitude}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Stats */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Floor Details</span>
                    <span className="font-medium">{surveyData.floor_details?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Documents</span>
                    <span className="font-medium">{surveyData.documents?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Comments</span>
                    <span className="font-medium">{surveyData.comments?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs for detailed information */}
          <div className="mt-6">
            <div className="border-b border-border">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'overview', label: 'Overview', icon: 'Eye' },
                  { id: 'survey-details', label: 'Survey Details', icon: 'ClipboardList' },
                  { id: 'surrounding', label: 'Surrounding', icon: 'MapPin' },
                  { id: 'rates', label: 'Rate Information', icon: 'DollarSign' },
                  { id: 'floors', label: 'Floor Details', icon: 'Building' },
                  { id: 'documents', label: 'Documents', icon: 'FileText' },
                  { id: 'comments', label: 'Comments', icon: 'MessageSquare' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === 'overview' && (
                <div className="bg-card rounded-lg border border-border p-6">
                  {/* Header */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Eye" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Survey Overview</h3>
                      <p className="text-sm text-muted-foreground">Key property attributes at a glance</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Property Type */}
                    <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                        <Icon name="Home" size={18} color="#2563eb" />
                        <h4 className="font-semibold text-blue-700">Property Type</h4>
                      </div>
                      <div className="p-4">
                        <div className="bg-white rounded-lg border border-blue-100 p-3">
                          <p className="text-sm font-bold text-foreground">{surveyData.survey_details?.property_type || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Locality */}
                    <div className="rounded-xl border border-green-100 bg-green-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-green-50">
                        <Icon name="MapPin" size={18} color="#16a34a" />
                        <h4 className="font-semibold text-green-700">Locality</h4>
                      </div>
                      <div className="p-4">
                        <div className="bg-white rounded-lg border border-green-100 p-3">
                          <p className="text-sm font-bold text-foreground">{surveyData.survey_details?.locality || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Structure Type */}
                    <div className="rounded-xl border border-purple-100 bg-purple-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-purple-50">
                        <Icon name="Building2" size={18} color="#7c3aed" />
                        <h4 className="font-semibold text-purple-700">Structure Type</h4>
                      </div>
                      <div className="p-4">
                        <div className="bg-white rounded-lg border border-purple-100 p-3">
                          <p className="text-sm font-bold text-foreground">{surveyData.survey_details?.structure?.type || 'N/A'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Flooring Type */}
                    <div className="rounded-xl border border-orange-100 bg-orange-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-orange-50">
                        <Icon name="Layers" size={18} color="#ea580c" />
                        <h4 className="font-semibold text-orange-600">Flooring Type</h4>
                      </div>
                      <div className="p-4">
                        <div className="bg-white rounded-lg border border-orange-100 p-3">
                          <p className="text-sm font-bold text-foreground">{surveyData.survey_details?.flooring?.type || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'survey-details' && (
                <div className="bg-card rounded-lg border border-border p-6">
                  {/* Header */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="ClipboardList" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Detailed Survey Information</h3>
                      <p className="text-sm text-muted-foreground">Complete overview of the surveyed property</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {/* Measurements */}
                    {surveyData.survey_details?.measurements && (
                      <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                          <Icon name="Ruler" size={18} color="#2563eb" />
                          <h4 className="font-semibold text-blue-700">Measurements</h4>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                            <div className="bg-white rounded-lg border border-blue-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">East</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.measurements.east_ft} ft</p>
                            </div>
                            <div className="bg-white rounded-lg border border-blue-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">West</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.measurements.west_ft} ft</p>
                            </div>
                            <div className="bg-white rounded-lg border border-blue-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">North</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.measurements.north_ft} ft</p>
                            </div>
                            <div className="bg-white rounded-lg border border-blue-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">South</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.measurements.south_ft} ft</p>
                            </div>
                            <div className="bg-white rounded-lg border border-blue-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">Plot Area</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.measurements.plot_area_uds_sft} sq ft</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Setbacks */}
                    {surveyData.survey_details?.setbacks && (
                      <div className="rounded-xl border border-green-100 bg-green-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-green-50">
                          <Icon name="Home" size={18} color="#16a34a" />
                          <h4 className="font-semibold text-green-700">Setbacks</h4>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="bg-white rounded-lg border border-green-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">East</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.setbacks.east_ft} ft</p>
                            </div>
                            <div className="bg-white rounded-lg border border-green-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">West</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.setbacks.west_ft} ft</p>
                            </div>
                            <div className="bg-white rounded-lg border border-green-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">North</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.setbacks.north_ft} ft</p>
                            </div>
                            <div className="bg-white rounded-lg border border-green-100 p-3">
                              <span className="text-xs text-muted-foreground block mb-1">South</span>
                              <p className="text-base font-bold text-foreground">{surveyData.survey_details.setbacks.south_ft} ft</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Road Information & Boundaries — side by side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {/* Road Information */}
                      {surveyData.survey_details?.road_info && (
                        <div className="rounded-xl border border-purple-100 bg-purple-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-purple-50">
                            <Icon name="Landmark" size={18} color="#7c3aed" />
                            <h4 className="font-semibold text-purple-700">Road Information</h4>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white rounded-lg border border-purple-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Road Type</span>
                                <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide">
                                  {surveyData.survey_details.road_info.type}
                                </span>
                              </div>
                              <div className="bg-white rounded-lg border border-purple-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Road Width</span>
                                <p className="text-base font-bold text-foreground">{surveyData.survey_details.road_info.width_ft} ft</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Boundaries */}
                      {surveyData.survey_details?.boundaries && (
                        <div className="rounded-xl border border-orange-100 bg-orange-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-orange-50">
                            <Icon name="Compass" size={18} color="#ea580c" />
                            <h4 className="font-semibold text-orange-600">Boundaries</h4>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white rounded-lg border border-orange-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">East</span>
                                <p className="text-sm font-bold text-foreground">{surveyData.survey_details.boundaries.east || '—'}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-orange-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">West</span>
                                <p className="text-sm font-bold text-foreground">{surveyData.survey_details.boundaries.west || '—'}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-orange-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">North</span>
                                <p className="text-sm font-bold text-foreground">{surveyData.survey_details.boundaries.north || '—'}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-orange-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">South</span>
                                <p className="text-sm font-bold text-foreground">{surveyData.survey_details.boundaries.south || '—'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Amenities — blue (same as Measurements) */}
                    {surveyData.survey_details?.amenities && (
                      <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                          <Icon name="Star" size={18} color="#2563eb" />
                          <h4 className="font-semibold text-blue-700">Amenities</h4>
                        </div>
                        <div className="p-4">
                          <div className="bg-white rounded-lg border border-blue-100 p-3">
                            <p className="text-sm text-foreground">{surveyData.survey_details.amenities}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Facilities — green (same as Setbacks) */}
                    {surveyData.survey_details?.facilities && (
                      <div className="rounded-xl border border-green-100 bg-green-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-green-50">
                          <Icon name="Wrench" size={18} color="#16a34a" />
                          <h4 className="font-semibold text-green-700">Facilities</h4>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {Object.entries(surveyData.survey_details.facilities).map(([key, value]) => (
                              <div key={key} className="flex items-center space-x-2 bg-white rounded-lg border border-green-100 px-3 py-2">
                                <Icon
                                  name={value ? "CheckCircle" : "XCircle"}
                                  size={15}
                                  color={value ? "var(--color-green-600)" : "var(--color-red-600)"}
                                />
                                <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Utility Bills — purple (same as Road Information) */}
                    {surveyData.survey_details?.utility_bills && (
                      <div className="rounded-xl border border-purple-100 bg-purple-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-purple-50">
                          <Icon name="Zap" size={18} color="#7c3aed" />
                          <h4 className="font-semibold text-purple-700">Utility Bills</h4>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {Object.entries(surveyData.survey_details.utility_bills).map(([key, value]) => (
                              <div key={key} className="flex items-center space-x-2 bg-white rounded-lg border border-purple-100 px-3 py-2">
                                <Icon
                                  name={value ? "CheckCircle" : "XCircle"}
                                  size={15}
                                  color={value ? "var(--color-green-600)" : "var(--color-red-600)"}
                                />
                                <span className="text-sm uppercase">{key}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Address Matching — orange (same as Boundaries) */}
                    {surveyData.survey_details?.address_matching !== undefined && (
                      <div className="rounded-xl border border-orange-100 bg-orange-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-orange-50">
                          <Icon name="MapPin" size={18} color="#ea580c" />
                          <h4 className="font-semibold text-orange-600">Address Matching</h4>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center space-x-2 bg-white rounded-lg border border-orange-100 px-3 py-2 w-fit">
                            <Icon
                              name={surveyData.survey_details.address_matching ? "CheckCircle" : "XCircle"}
                              size={15}
                              color={surveyData.survey_details.address_matching ? "var(--color-green-600)" : "var(--color-red-600)"}
                            />
                            <span className="text-sm font-medium">
                              {surveyData.survey_details.address_matching ? "Address Matches" : "Address Does Not Match"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'documents' && (
                <div className="bg-card rounded-lg border border-border p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="FileText" size={20} color="var(--color-primary)" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">Documents</h3>
                        <p className="text-sm text-muted-foreground">Uploaded survey documents and images</p>
                      </div>
                    </div>
                    {surveyData.documents?.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadAllImages}
                        disabled={isDownloadingAll}
                        iconName="Download"
                        iconPosition="left"
                      >
                        {isDownloadingAll ? 'Downloading...' : 'Download All'}
                      </Button>
                    )}
                  </div>

                  {surveyData.documents?.length > 0 ? (
                    <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                        <Icon name="Images" size={18} color="#2563eb" />
                        <h4 className="font-semibold text-blue-700">Uploaded Files</h4>
                        <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{surveyData.documents.length} file{surveyData.documents.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="p-4 space-y-3">
                        {surveyData.documents.map((doc, index) => (
                          <div key={index} className="bg-white rounded-lg border border-blue-100 p-4 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                <Icon name="FileImage" size={18} color="#2563eb" />
                              </div>
                              <div>
                                <p className="font-semibold text-foreground text-sm">{doc.title}</p>
                                {doc.description && <p className="text-xs text-muted-foreground">{doc.description}</p>}
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {formatDateTime(doc.uploaded_at)} · <span className="capitalize">{doc.document_type}</span>
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDocument(doc)}
                            >
                              View
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                        <Icon name="Images" size={18} color="#2563eb" />
                        <h4 className="font-semibold text-blue-700">Uploaded Files</h4>
                      </div>
                      <div className="p-6 text-center">
                        <p className="text-muted-foreground text-sm">No documents uploaded yet.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'surrounding' && (
                <div className="bg-card rounded-lg border border-border p-6">
                  {/* Header */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="MapPin" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Surrounding Details</h3>
                      <p className="text-sm text-muted-foreground">Environmental and neighbourhood context</p>
                    </div>
                  </div>

                  {surveyData.surrounding_details ? (
                    <div className="space-y-5">
                      {/* Surrounding Features */}
                      {surveyData.surrounding_details.features?.length > 0 && (
                        <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                            <Icon name="Layers" size={18} color="#2563eb" />
                            <h4 className="font-semibold text-blue-700">Surrounding Features</h4>
                            <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{surveyData.surrounding_details.features.length} feature{surveyData.surrounding_details.features.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="p-4 space-y-3">
                            {surveyData.surrounding_details.features.map((feature, index) => (
                              <div key={index} className="bg-white rounded-lg border border-blue-100 p-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div>
                                    <span className="text-xs text-muted-foreground block mb-1">Type</span>
                                    <p className="text-sm font-bold text-foreground capitalize">{feature.type?.replace(/_/g, ' ')}</p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-muted-foreground block mb-1">Coverage</span>
                                    <p className="text-sm font-bold text-foreground capitalize">{feature.coverage ? `${feature.coverage}` : '—' }</p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-muted-foreground block mb-1">Width</span>
                                    <p className="text-sm font-bold text-foreground">{feature.width_ft ? `${feature.width_ft} ft` : '—'}</p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-muted-foreground block mb-1">Distance</span>
                                    <p className="text-sm font-bold text-foreground">{feature.distance_ft ? `${feature.distance_ft} ft` : '—'}</p>
                                  </div>
                                  {feature.os_number && (
                                    <div className="col-span-2 md:col-span-4">
                                      <span className="text-xs text-muted-foreground block mb-1">OS Number</span>
                                      <p className="text-sm font-bold text-foreground font-mono">{feature.os_number_value}</p>
                                    </div>
                                  )}
                                  {feature.other_details && (
                                    <div className="col-span-2 md:col-span-4">
                                      <span className="text-xs text-muted-foreground block mb-1">Other Details</span>
                                      <p className="text-sm text-foreground">{feature.other_details}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Negative Remarks */}
                      {surveyData.surrounding_details.negative_remarks && (
                        <div className="rounded-xl border border-red-100 bg-red-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-red-50">
                            <Icon name="AlertTriangle" size={18} color="#dc2626" />
                            <h4 className="font-semibold text-red-700">Negative Remarks</h4>
                          </div>
                          <div className="p-4">
                            <div className="bg-white rounded-lg border border-red-100 p-3">
                              <p className="text-sm text-foreground">{surveyData.surrounding_details.negative_remarks}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                        <Icon name="Layers" size={18} color="#2563eb" />
                        <h4 className="font-semibold text-blue-700">Surrounding Features</h4>
                      </div>
                      <div className="p-6 text-center">
                        <p className="text-muted-foreground text-sm">No surrounding details available.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'rates' && (
                <div className="bg-card rounded-lg border border-border p-6">
                  {/* Header */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="DollarSign" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Rate Information</h3>
                      <p className="text-sm text-muted-foreground">Market rates and valuation references</p>
                    </div>
                  </div>

                  {surveyData.rate_information ? (
                    <div className="space-y-5">
                      {/* Land Rate */}
                      {surveyData.rate_information.land_rate && (
                        <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                            <Icon name="Map" size={18} color="#2563eb" />
                            <h4 className="font-semibold text-blue-700">Land Rate <span className="font-normal text-blue-500 text-xs">(per sq ft)</span></h4>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white rounded-lg border border-blue-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Minimum</span>
                                <p className="text-base font-bold text-foreground">₹{surveyData.rate_information.land_rate.min}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-blue-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Maximum</span>
                                <p className="text-base font-bold text-foreground">₹{surveyData.rate_information.land_rate.max}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Flat/Villa Composite Rate */}
                      {surveyData.rate_information.flat_villa_composite_rate && (
                        <div className="rounded-xl border border-green-100 bg-green-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-green-50">
                            <Icon name="Building2" size={18} color="#16a34a" />
                            <h4 className="font-semibold text-green-700">Flat / Villa Composite Rate <span className="font-normal text-green-500 text-xs">(per sq ft)</span></h4>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white rounded-lg border border-green-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Minimum</span>
                                <p className="text-base font-bold text-foreground">₹{surveyData.rate_information.flat_villa_composite_rate.min ? `₹${surveyData.rate_information.flat_villa_composite_rate.min}` : 'N/A'}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-green-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Maximum</span>
                                <p className="text-base font-bold text-foreground">₹{surveyData.rate_information.flat_villa_composite_rate.max ? `₹${surveyData.rate_information.flat_villa_composite_rate.min}` : 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Commercial Rate */}
                      {surveyData.rate_information.commercial_rate && (
                        <div className="rounded-xl border border-purple-100 bg-purple-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-purple-50">
                            <Icon name="Store" size={18} color="#7c3aed" />
                            <h4 className="font-semibold text-purple-700">Commercial Rate <span className="font-normal text-purple-400 text-xs">(per sq ft)</span></h4>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white rounded-lg border border-purple-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Minimum</span>
                                <p className="text-base font-bold text-foreground">{surveyData.rate_information.commercial_rate.min ? `₹${surveyData.rate_information.commercial_rate.min}` : 'N/A'}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-purple-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Maximum</span>
                                <p className="text-base font-bold text-foreground">{surveyData.rate_information.commercial_rate.max ? `₹${surveyData.rate_information.commercial_rate.max}` : 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Commercial Rent */}
                      {surveyData.rate_information.commercial_rent && (
                        <div className="rounded-xl border border-orange-100 bg-orange-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-orange-50">
                            <Icon name="Receipt" size={18} color="#ea580c" />
                            <h4 className="font-semibold text-orange-600">Commercial Rent <span className="font-normal text-orange-400 text-xs">(per month)</span></h4>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white rounded-lg border border-orange-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Minimum</span>
                                <p className="text-base font-bold text-foreground">{surveyData.rate_information.commercial_rent.min ? `₹${surveyData.rate_information.commercial_rent.min}` : 'N/A'}</p>
                              </div>
                              <div className="bg-white rounded-lg border border-orange-100 p-3">
                                <span className="text-xs text-muted-foreground block mb-1">Maximum</span>
                                <p className="text-base font-bold text-foreground">{surveyData.rate_information.commercial_rent.max ? `₹${surveyData.rate_information.commercial_rent.max}` : 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* References */}
                      {surveyData.rate_information.references?.length > 0 && (
                        <div className="rounded-xl border border-green-100 bg-green-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-green-50">
                            <Icon name="BookOpen" size={18} color="#16a34a" />
                            <h4 className="font-semibold text-green-700">Rate References</h4>
                          </div>
                          <div className="p-4 space-y-2">
                            {surveyData.rate_information.references.map((reference, index) => (
                              <div key={index} className="bg-white rounded-lg border border-green-100 px-4 py-3">
                                <p className="text-sm text-foreground">{reference}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Remarks */}
                      {surveyData.rate_information.remarks && (
                        <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                          <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                            <Icon name="MessageCircle" size={18} color="#2563eb" />
                            <h4 className="font-semibold text-blue-700">Remarks</h4>
                          </div>
                          <div className="p-4">
                            <div className="bg-white rounded-lg border border-blue-100 p-3">
                              <p className="text-sm text-foreground">{surveyData.rate_information.remarks}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                        <Icon name="Map" size={18} color="#2563eb" />
                        <h4 className="font-semibold text-blue-700">Rate Information</h4>
                      </div>
                      <div className="p-6 text-center">
                        <p className="text-muted-foreground text-sm">No rate information available.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'floors' && (
                <div className="bg-card rounded-lg border border-border p-6">
                  {/* Header */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Building" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Floor Details</h3>
                      <p className="text-sm text-muted-foreground">Building configuration and floor-wise breakdown</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {/* Building Configuration */}
                    {surveyData.survey_details?.building_config?.buildings?.length > 0 && (
                      <div className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-blue-50">
                          <Icon name="Building2" size={18} color="#2563eb" />
                          <h4 className="font-semibold text-blue-700">Building Configuration</h4>
                          <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{surveyData.survey_details.building_config.buildings.length} building{surveyData.survey_details.building_config.buildings.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {surveyData.survey_details.building_config.buildings.map((building, bIdx) => (
                              <div key={bIdx} className="bg-white rounded-lg border border-blue-100 p-4">
                                <p className="font-semibold text-foreground text-sm mb-3">{building.name}</p>
                                <div className="flex items-center space-x-2 mb-3">
                                  <span className="text-xs text-muted-foreground">Structure Type:</span>
                                  <span className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full font-medium">{building.structure_type}</span>
                                </div>
                                {building.floors?.length > 0 && (
                                  <div>
                                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                      <span>Floors</span>
                                      <span>Flooring Type</span>
                                    </div>
                                    <div className="space-y-1">
                                      {building.floors.map((floor, fIdx) => (
                                        <div key={fIdx} className="flex justify-between text-xs py-1 border-b border-blue-50 last:border-0">
                                          <span className="font-bold text-foreground">{floor.name}</span>
                                          <span className="text-foreground font-medium">{floor.flooring}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Floor Details grouped by building */}
                    {surveyData.floor_details?.length > 0 ? (
                      <div className="rounded-xl border border-green-100 bg-green-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-green-50">
                          <Icon name="Layers" size={18} color="#16a34a" />
                          <h4 className="font-semibold text-green-700">Floor Details</h4>
                          <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{surveyData.floor_details.length} floor{surveyData.floor_details.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="p-4 space-y-3">
                          {Object.entries(
                            surveyData.floor_details.reduce((acc, floor) => {
                              const key = floor.building_label || 'General';
                              if (!acc[key]) acc[key] = [];
                              acc[key].push(floor);
                              return acc;
                            }, {})
                          ).map(([buildingLabel, floors]) => (
                            <div key={buildingLabel} className="bg-white rounded-lg border border-green-100 overflow-hidden">
                              <div className="flex items-center space-x-2 px-4 py-2.5 bg-green-50/60 border-b border-green-100">
                                <Icon name="Building2" size={14} color="#16a34a" />
                                <span className="font-semibold text-foreground text-sm">{buildingLabel}</span>
                                <span className="text-xs text-muted-foreground ml-1">({floors.length} floor{floors.length !== 1 ? 's' : ''})</span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-green-50/40">
                                    <tr>
                                      {['Floor', 'Units', 'Usage', 'BUA/SBUA (sq ft)', 'Age (yrs)', 'Rent/Lease', 'Occupied By', 'Stair/Balcony/Portico'].map(h => (
                                        <th key={h} className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-green-50">
                                    {floors.map((floor, fIdx) => (
                                      <tr key={fIdx} className="hover:bg-green-50/30">
                                        <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{floor.floor_label}</td>
                                        <td className="px-4 py-3 text-foreground">{floor.units}</td>
                                        <td className="px-4 py-3 text-foreground capitalize">{floor.usage}</td>
                                        <td className="px-4 py-3 text-foreground font-semibold">{floor.bua_sbua}</td>
                                        <td className="px-4 py-3 text-foreground">{floor.age_in_years}</td>
                                        <td className="px-4 py-3 text-foreground whitespace-nowrap">{floor.rent_lease_amount ? `₹${floor.rent_lease_amount}` : '—'}</td>
                                        <td className="px-4 py-3 text-foreground">{floor.occupied_by_period_years}</td>
                                        <td className="px-4 py-3 text-foreground">{floor.stair_balcony_portico_area}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-green-100 bg-green-50/40 overflow-hidden">
                        <div className="flex items-center space-x-2 px-5 py-3 bg-green-50">
                          <Icon name="Layers" size={18} color="#16a34a" />
                          <h4 className="font-semibold text-green-700">Floor Details</h4>
                        </div>
                        <div className="p-6 text-center">
                          <p className="text-muted-foreground text-sm">No floor details available.</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'comments' && (
                <div className="bg-card rounded-lg border border-border p-6">
                  {/* Header */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Comments</h3>
                      <p className="text-sm text-muted-foreground">Notes and observations from the survey team</p>
                    </div>
                  </div>

                  {surveyData.comments?.length > 0 ? (
                    <div className="rounded-xl border border-purple-100 bg-purple-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-purple-50">
                        <Icon name="MessagesSquare" size={18} color="#7c3aed" />
                        <h4 className="font-semibold text-purple-700">All Comments</h4>
                        <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">{surveyData.comments.length} comment{surveyData.comments.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="p-4 space-y-3">
                        {surveyData.comments.map((comment, index) => (
                          <div key={index} className="bg-white rounded-lg border border-purple-100 p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                  <Icon name="User" size={14} color="#7c3aed" />
                                </div>
                                <span className="font-semibold text-foreground text-sm">{comment.author.username}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">{formatDateTime(comment.created_at)}</span>
                            </div>
                            <p className="text-sm text-foreground pl-9">{comment.comment}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-purple-100 bg-purple-50/40 overflow-hidden">
                      <div className="flex items-center space-x-2 px-5 py-3 bg-purple-50">
                        <Icon name="MessagesSquare" size={18} color="#7c3aed" />
                        <h4 className="font-semibold text-purple-700">All Comments</h4>
                      </div>
                      <div className="p-6 text-center">
                        <p className="text-muted-foreground text-sm">No comments yet.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {isDocumentModalOpen && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold text-foreground">{selectedDocument.title}</h3>
                <p className="text-sm text-muted-foreground">{selectedDocument.description}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeDocumentModal}
                iconName="X"
              >
                Close
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                {/* Document Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Document Type</label>
                    <p className="text-foreground capitalize">{selectedDocument.document_type}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Uploaded At</label>
                    <p className="text-foreground">{formatDateTime(selectedDocument.uploaded_at)}</p>
                  </div>
                </div>

                {/* Image Display */}
                <div className="flex justify-center">
                  <div className="relative max-w-full">
                    {documentImageSrc ? (
                      <img
                        src={documentImageSrc}
                        alt={selectedDocument.title}
                        className="max-w-full max-h-[60vh] object-contain rounded-lg border border-border shadow-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="flex items-center justify-center w-full h-64">
                        <Icon name="Loader2" size={24} className="animate-spin" color="var(--color-primary)" />
                      </div>
                    )}
                    {/* Error fallback */}
                    <div
                      className="hidden items-center justify-center w-full h-64 bg-muted rounded-lg border border-border"
                      style={{ display: 'none' }}
                    >
                      <div className="text-center">
                        <Icon name="FileX" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                        <p className="text-muted-foreground">Unable to load image</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Path: {selectedDocument.file_path}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Download Button */}
                <div className="flex justify-center">
                  <Button
                    variant="default"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = getMediaUrl(selectedDocument.file_path);
                      link.download = selectedDocument.title;
                      link.target = '_blank';
                      link.click();
                    }}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Image
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldSurvey;